import { ExclamationCircleOutlined, PoweroffOutlined } from '@ant-design/icons';
import { geti18nText, NyRequestResolver, NySearchField, NySession, RESPONSE } from '@nybble/nyreact';
import { Button, Card, Col, Form, InputNumber, Modal, notification, Row, Spin } from 'antd';
import Countdown from 'antd/lib/statistic/Countdown';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import SSE from '../../components/layout/see';
import Statistic from '../../components/statistic';
import { CONSTANTS_REQ, ENUMS_PROGRAM_OUTPUT, PROGRAM_OUTPUT } from '../../utils/Constants';

const DeviceManagement = (props: any) => {
    const { deviceId } = props;
    const [deviceStatus, setDeviceStatus] = useState<any>([]);
    const [loading, setLoading] = useState(false);
    const [timer, setTimer] = useState<any>(undefined);
    const [diagnosticModalVisible, setDiagnosticModalVisible] = useState(false);

    const eventSourceRef = useRef<any>(null);

    const [diagnosticsForm] = Form.useForm();

    useEffect(() => {
        getLatestDeviceStatus();
    }, []);

    useEffect(() => {
        if (props.onRefresh != undefined) {
            getLatestDeviceStatus();
        }
    }, [props.onRefresh]);

    function waitForStatusUpdateOrTimeout() {
        eventSourceRef.current = new SSE(CONSTANTS_REQ.DEVICE.LISTEN_DEVICE_STATUS + '?id=' + deviceId, {
            headers: {
                Authorization: 'Bearer ' + NySession.getUserToken(),
            },
        });

        eventSourceRef.current.onmessage = (e: any) => {
            if (e && e.data == '' + deviceId) {
                eventSourceRef.current.close();
                stopTimer();
            }
        };

        eventSourceRef.current.onerror = (e: any) => {
            console.log('sse error', e);
            eventSourceRef.current.close();
            stopTimer();
        };

        eventSourceRef.current.stream();
        startTimer();
    }

    function getLatestDeviceStatus() {
        setLoading(true);
        NyRequestResolver.requestGet(CONSTANTS_REQ.DEVICE.LATEST_DEVICE_STATUS, {
            deviceId: deviceId,
        }).then((result: any) => {
            setLoading(false);
            if (result.status === RESPONSE.OK) {
                setDeviceStatus(result.data);
            }
        });
    }

    function powerControl(relay: number, state: boolean) {
        setLoading(true);
        NyRequestResolver.requestPost(CONSTANTS_REQ.DEVICE.SEND_POWER_CONTROL_REQUEST, undefined, {
            id: deviceId,
            relay: relay,
            state: state,
        }).then((result: any) => {
            setLoading(false);
            if (result.status === RESPONSE.OK) {
                waitForStatusUpdateOrTimeout();
            }
        });
    }

    function status() {
        setLoading(true);
        NyRequestResolver.requestPost(CONSTANTS_REQ.DEVICE.SEND_STATUS_REQUEST, undefined, {
            id: deviceId,
        }).then((result: any) => {
            setLoading(false);
            if (result.status === RESPONSE.OK) {
                waitForStatusUpdateOrTimeout();
            }
        });
    }

    function additionalData() {
        setLoading(true);
        NyRequestResolver.requestPost(CONSTANTS_REQ.DEVICE.SEND_ADDITIONAL_DATA_REQUEST, undefined, {
            id: deviceId,
        }).then((result: any) => {
            setLoading(false);
            if (result.status === RESPONSE.OK) {
                additionalDataconfirm();
            }
        });
    }

    function emergency(state: boolean) {
        setLoading(true);
        NyRequestResolver.requestPost(CONSTANTS_REQ.DEVICE.SEND_EMERGENCY, undefined, {
            ids: [deviceId],
            state: state,
        }).then((result: any) => {
            setLoading(false);
            if (result.status === RESPONSE.OK) {
                waitForStatusUpdateOrTimeout();
            }
        });
    }

    function syncDevice() {
        setLoading(true);
        NyRequestResolver.requestPost(CONSTANTS_REQ.DEVICE.SYNC, undefined, {
            projectId: NySession.getProjectId(),
            deviceIds: [deviceId],
        }).then((result) => {
            setLoading(false);
            if (result.status === RESPONSE.OK) {
                okNotification(geti18nText('app.default.sync.ok.desc'));
            } else {
                error(geti18nText('app.default.sync.nok.desc'));
            }
        });
    }

    function diagnosticMode() {
        diagnosticsForm.validateFields().then((values: any) => {
            setLoading(true);
            NyRequestResolver.requestPost(CONSTANTS_REQ.DEVICE.SEND_DIAGNOSTIC_MODE, undefined, {
                id: deviceId,
                relay: Number(values.diagnosticsRelay.id),
                duration: values.diagnosticsDuration,
            }).then((result: any) => {
                setLoading(false);
                hideDiagnosticModal();
                if (result.status === RESPONSE.OK) {
                    okNotification(geti18nText('app.default.diagnostic.ok.desc'));
                } else {
                    error(geti18nText('app.default.diagnostic.nok.desc'));
                }
            });
        });
    }

    function startTimer() {
        setLoading(true);
        setTimer(Date.now() + 61 * 1000);
    }

    function stopTimer() {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = undefined;
        }
        setTimer(undefined);
        getLatestDeviceStatus();
    }

    function okNotification(message: any = undefined) {
        notification.success({
            message: geti18nText('app.default.save.ok'),
            description: message ? message : geti18nText('app.default.save.ok.desc'),
            duration: 3,
        });
    }

    function error(message: any) {
        notification.error({
            message: geti18nText('app.default.error'),
            description: message,
            duration: 0,
        });
    }

    const additionalDataconfirm = () => {
        Modal.confirm({
            title: geti18nText('app.default.save.ok'),
            icon: <ExclamationCircleOutlined />,
            content: geti18nText('devices.status.button.additional.data.executed'),
            cancelButtonProps: { style: { display: 'none' } },
        });
    };

    const hideDiagnosticModal = () => {
        setDiagnosticModalVisible(false);
        diagnosticsForm.resetFields();
    };

    return (
        <>
            {timer !== undefined && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(255, 255, 255, 0)',
                        zIndex: 1000,
                        pointerEvents: 'auto',
                    }}
                />
            )}
            <Spin
                spinning={timer != undefined}
                size="large"
                tip={
                    <Countdown
                        title={geti18nText('devices.status.table.waiting.status')}
                        value={timer}
                        onFinish={stopTimer}
                        format="mm:ss"
                    />
                }
            >
                <Row gutter={[12, 12]} style={{ marginTop: '12px' }}>
                    <Col xs={12} xl={4}>
                        <Card bordered={true} style={{ borderRadius: '1em', height: '100%' }}>
                            <Statistic
                                title={geti18nText('devices.status.table.column.lastFetched')}
                                value={
                                    deviceStatus.insertedDate
                                        ? moment(deviceStatus.insertedDate).format('DD.MM.YYYY HH:mm:ss')
                                        : ''
                                }
                                loading={loading}
                            ></Statistic>
                        </Card>
                    </Col>
                    <Col xs={12} xl={4}>
                        <Card bordered={true} style={{ borderRadius: '1em', height: '100%' }}>
                            <Statistic
                                title={geti18nText('devices.status.table.column.cb')}
                                value={
                                    deviceStatus.cb ? (
                                        <PoweroffOutlined style={{ color: 'green' }} />
                                    ) : (
                                        <PoweroffOutlined style={{ color: 'red' }} />
                                    )
                                }
                                loading={loading}
                            ></Statistic>
                        </Card>
                    </Col>
                    <Col xs={12} xl={4}>
                        <Card bordered={true} style={{ borderRadius: '1em', height: '100%' }}>
                            <Statistic
                                title={geti18nText('devices.status.table.column.r1')}
                                value={
                                    deviceStatus.r1 ? (
                                        <PoweroffOutlined style={{ color: 'green' }} />
                                    ) : (
                                        <PoweroffOutlined style={{ color: 'red' }} />
                                    )
                                }
                                loading={!loading}
                            ></Statistic>
                        </Card>
                    </Col>
                    <Col xs={12} xl={4}>
                        <Card bordered={true} style={{ borderRadius: '1em', height: '100%' }}>
                            <Statistic
                                title={geti18nText('devices.status.table.column.r2')}
                                value={
                                    deviceStatus.r2 ? (
                                        <PoweroffOutlined style={{ color: 'green' }} />
                                    ) : (
                                        <PoweroffOutlined style={{ color: 'red' }} />
                                    )
                                }
                                loading={loading}
                            ></Statistic>
                        </Card>
                    </Col>
                    <Col xs={12} xl={4}>
                        <Card bordered={true} style={{ borderRadius: '1em', height: '100%' }}>
                            <Statistic
                                title={geti18nText('devices.status.table.column.emergency')}
                                value={
                                    deviceStatus.emergency ? (
                                        <span style={{ color: 'red' }}>{geti18nText('app.default.button.yes')}</span>
                                    ) : (
                                        <span style={{ color: 'green' }}>{geti18nText('app.default.button.no')}</span>
                                    )
                                }
                                loading={loading}
                            ></Statistic>
                        </Card>
                    </Col>
                </Row>
                <Row gutter={[12, 12]} style={{ marginTop: '12px' }}>
                    <Col>
                        <Card bordered={true} style={{ borderRadius: '1em', height: '100%' }}>
                            <Statistic
                                title={geti18nText('devices.status.button.group.management')}
                                value={
                                    <Row gutter={12}>
                                        <Col>
                                            <Button
                                                style={{ width: '100%', textAlign: 'center' }}
                                                disabled={loading || deviceStatus.emergency}
                                                onClick={() => powerControl(PROGRAM_OUTPUT.CB, !deviceStatus.cb)}
                                            >
                                                {(deviceStatus.cb
                                                    ? geti18nText('devices.status.button.turn.off')
                                                    : geti18nText('devices.status.button.turn.on')) +
                                                    ' ' +
                                                    geti18nText('enum.programOutput.' + PROGRAM_OUTPUT.CB)}
                                            </Button>
                                        </Col>
                                        <Col>
                                            <Button
                                                style={{ width: '100%', textAlign: 'center' }}
                                                disabled={loading || deviceStatus.emergency}
                                                onClick={() => powerControl(PROGRAM_OUTPUT.R1, !deviceStatus.r1)}
                                            >
                                                {(deviceStatus.r1
                                                    ? geti18nText('devices.status.button.turn.off')
                                                    : geti18nText('devices.status.button.turn.on')) +
                                                    ' ' +
                                                    geti18nText('enum.programOutput.' + PROGRAM_OUTPUT.R1)}
                                            </Button>
                                        </Col>
                                        <Col>
                                            <Button
                                                style={{ width: '100%', textAlign: 'center' }}
                                                disabled={loading || deviceStatus.emergency}
                                                onClick={() => powerControl(PROGRAM_OUTPUT.R2, !deviceStatus.r2)}
                                            >
                                                {(deviceStatus.r2
                                                    ? geti18nText('devices.status.button.turn.off')
                                                    : geti18nText('devices.status.button.turn.on')) +
                                                    ' ' +
                                                    geti18nText('enum.programOutput.' + PROGRAM_OUTPUT.R2)}
                                            </Button>
                                        </Col>
                                        <Col>
                                            <Button
                                                style={{ width: '100%', textAlign: 'center' }}
                                                type="primary"
                                                disabled={loading}
                                                onClick={() => emergency(!deviceStatus.emergency)}
                                                className={
                                                    deviceStatus.emergency ? 'ant-btn-green' : 'ant-btn-dangerous'
                                                }
                                            >
                                                {deviceStatus.emergency
                                                    ? geti18nText('devices.table.emergencyCancel')
                                                    : geti18nText('devices.table.emergencyOff')}
                                            </Button>
                                        </Col>
                                    </Row>
                                }
                                loading={loading}
                            ></Statistic>
                        </Card>
                    </Col>
                    <Col>
                        <Card bordered={true} style={{ borderRadius: '1em', height: '100%' }}>
                            <Statistic
                                title={geti18nText('devices.status.button.group.additional')}
                                value={
                                    <Row gutter={12}>
                                        <Col>
                                            <Button
                                                style={{ width: '100%', textAlign: 'center' }}
                                                disabled={loading}
                                                onClick={() => status()}
                                            >
                                                {geti18nText('devices.status.button.status')}
                                            </Button>
                                        </Col>
                                        <Col>
                                            <Button
                                                style={{ width: '100%', textAlign: 'center' }}
                                                disabled={loading}
                                                onClick={() => additionalData()}
                                            >
                                                {geti18nText('devices.status.button.additional.data')}
                                            </Button>
                                        </Col>
                                        <Col>
                                            <Button
                                                style={{ width: '100%', textAlign: 'center' }}
                                                disabled={loading}
                                                onClick={() => syncDevice()}
                                            >
                                                {geti18nText('app.default.button.sync')}
                                            </Button>
                                        </Col>
                                        <Col>
                                            <Button
                                                style={{ width: '100%', textAlign: 'center' }}
                                                disabled={loading}
                                                onClick={() => setDiagnosticModalVisible(true)}
                                            >
                                                {geti18nText('devices.status.button.diagnostic.mode')}
                                            </Button>
                                        </Col>
                                    </Row>
                                }
                                loading={loading}
                            ></Statistic>
                        </Card>
                    </Col>
                </Row>
            </Spin>
            <Modal
                title={geti18nText('devices.status.button.diagnostic.mode')}
                visible={diagnosticModalVisible}
                onCancel={() => hideDiagnosticModal()}
                okText={geti18nText('app.default.button.start')}
                onOk={() => diagnosticMode()}
                destroyOnClose
            >
                <Form layout="vertical" form={diagnosticsForm}>
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item
                                name="diagnosticsRelay"
                                label={geti18nText('devices.diagnostics.relay')}
                                rules={[
                                    {
                                        required: true,
                                        message: geti18nText('app.default.required'),
                                    },
                                ]}
                            >
                                <NySearchField
                                    options={ENUMS_PROGRAM_OUTPUT()}
                                    map={{ id: 'id', label: 'text' }}
                                    searchBy="text"
                                    onChange={(value: any) => {
                                        if (value.id < 0) {
                                            diagnosticsForm.setFieldsValue({ diagnosticsRelay: undefined });
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="diagnosticsDuration"
                                label={geti18nText('devices.diagnostics.duration')}
                                rules={[
                                    {
                                        required: true,
                                        message: geti18nText('app.default.required'),
                                    },
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={30}
                                    max={300}
                                    formatter={(value) => `${value}`.replace(/(\.[^]+)|([^0-9])/g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </>
    );
};

export default DeviceManagement;
